import { useMemo } from 'react';

import {
  DropdownFilterChipRoot,
  DropdownFilterChip,
  DropdownFilterChipMenu,
  DropdownFilterChipMenuItem,
} from './DropdownFilterChip';
import type { ChipSize } from './Chip';
import { Portal } from '../Portal';

export type FilterChipItem<Value extends string> = {
  label: string;
  value: Value;
};

type SelectFilterChipProps<Value extends string> = {
  size?: ChipSize;
  placeholder: string;
  selected?: string;
  disabled?: boolean;
  items: ReadonlyArray<FilterChipItem<Value>>;
  onSelect: (value: Value) => void;
  onClear?: () => void;
  ['data-testid']?: string;
  ['aria-label']?: string;
  className?: string;
  hasMenu?: boolean;
  css?: string;
  isLoading?: boolean;
  isPortal?: boolean;
};

export function SelectFilterChip<Value extends string>(
  props: SelectFilterChipProps<Value>
): React.ReactElement {
  const {
    size = 'md',
    disabled = false,
    placeholder,
    onClear,
    onSelect,
    selected,
    hasMenu,
    items = [],
    isPortal = false,
    ...rest
  } = props;

  const itemValues = useMemo(() => {
    return items.reduce((map: Map<Value | string, string>, item) => {
      map.set(item.value, item.label);
      return map;
    }, new Map());
  }, [items]);
  const hasSelection = !!selected && itemValues.has(selected);
  const textContent = itemValues.get(selected) ?? placeholder;

  const dropdownMenu = (
    <DropdownFilterChipMenu>
      {items.map(({ label, value }) => (
        <DropdownFilterChipMenuItem
          key={value}
          value={value}
          isSelected={selected === value}
          onSelect={() => onSelect(value)}
        >
          {label}
        </DropdownFilterChipMenuItem>
      ))}
    </DropdownFilterChipMenu>
  );

  return (
    <DropdownFilterChipRoot>
      <DropdownFilterChip
        {...rest}
        hasMenu={hasMenu}
        size={size}
        hasSelection={hasSelection}
        disabled={disabled}
        onClear={onClear}
      >
        {textContent}
      </DropdownFilterChip>
      {isPortal ? <Portal>{dropdownMenu}</Portal> : dropdownMenu}
    </DropdownFilterChipRoot>
  );
}
